import React, { createContext, useContext, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import ProgressBar from '../intake/ProgressBar';
import PreviewStepFrame from './PreviewStepFrame';
import PreviewProgress from './PreviewProgress';
import PreviewInterstitial from './PreviewInterstitial';
import { PREVIEW_FLOWS, metaFor, stageIndexFor, insertInterstitials, withOptionIcons } from '@/data/previewMeta';
import './preview.css';

// §preview — the omzo-style presentation layer, opt-in per route.
//
// Every intake form calls `usePreview()` and routes four things through it:
// the flow (to insert marketing interstitials), the progress bar, the step
// wrapper (headline + illustration frame + Back button) and the root class.
// Outside a <PreviewProvider> all of these are identity/no-ops, so the live
// routes render exactly as before. Inside one (/preview/<flow>) the same form
// gets the preview chrome. Nothing here changes what is submitted.

const PreviewContext = createContext(null);

const INERT = {
  active: false,
  def: null,
  rootClass: 'bg-white',
  wrapFlow: (flow) => flow,
  withIcons: (q) => q,
  frame: (question, content) => content,
  progress: (question, current, total) => <ProgressBar current={current} total={total} />,
};

export function usePreview() {
  return useContext(PreviewContext) || INERT;
}

export function PreviewProvider({ flow, children }) {
  const def = PREVIEW_FLOWS[flow];
  const value = useMemo(() => {
    if (!def) return INERT;
    return {
      active: true,
      def,
      rootClass: 'wl-preview',
      wrapFlow: (f) => insertInterstitials(def, f),
      // QuestionStep renders option icons when question.showIcons is set.
      withIcons: (q) => {
        const m = q ? metaFor(def, q.id) : null;
        const withIcons = withOptionIcons(q, m);
        return withIcons !== q ? { ...withIcons, showIcons: true } : q;
      },
      frame: (question, content, onBack) => {
        if (!question) return content;
        if (question.type === 'complete') return content;
        const chromeless = question.type === 'checkout' || question.type === 'preview-interstitial';
        return (
          <PreviewStepFrame meta={chromeless ? null : metaFor(def, question.id)} onBack={onBack}>
            {content}
          </PreviewStepFrame>
        );
      },
      progress: (question) => (
        <PreviewProgress stages={def.stages} active={question ? stageIndexFor(def, question.id) : 0} />
      ),
    };
  }, [def]);
  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
}

// /preview/:flow → the live form for that flow, inside the provider.
// `forms` maps flow key → component (App.js supplies it to avoid an import cycle).
export function PreviewRoute({ forms }) {
  const { flow } = useParams();
  const Form = forms[flow];
  if (!Form || !PREVIEW_FLOWS[flow]) return <Navigate to="/" replace />;
  return (
    <PreviewProvider flow={flow}>
      <Form />
    </PreviewProvider>
  );
}

export { PreviewStepFrame, PreviewProgress, PreviewInterstitial };
